import { computed, Injectable, signal } from '@angular/core'

import { UserRole } from '@yoshu-lib/constants/roles.enum'

import { User } from '@yoshu-lib/models/user.model'

import { LoginSuccessResponse, MeResponse } from './auth.types'

const USER_DATA_KEY = 'yoshu_user_data'

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private _user = signal<User | null>(null)
  private _isLoggingIn = signal<boolean>(false)

  readonly user = this._user.asReadonly()
  readonly isLoggingIn = this._isLoggingIn.asReadonly()
  readonly currentUser = computed(() => this._user())
  readonly role = computed(() => this._user()?.role ?? null)
  readonly isAdmin = computed(() => this.role() === UserRole.Admin)
  readonly isLoggedIn = computed(() => this._user() !== null)
  readonly isYoshuStaff = computed(
    () => this._user()?.email.toLowerCase().endsWith('@yoshu.ai') ?? false
  )
  readonly groups = computed(() => this._user()?.groups ?? [])

  hasGroup(idOrSlug: string): boolean {
    // Match by slug (machine identifier) primarily. Fall back to name for older
    // persisted session payloads where the API hadn't started returning slug yet,
    // and for groups that have never been given a display name.
    const groups = this.groups()
    return groups.some((g) => {
      const slug = g.slug ?? g.name
      return String(g.id) === idOrSlug || slug === idOrSlug
    })
  }

  setIsLoggingIn(value: boolean): void {
    this._isLoggingIn.set(value)
  }

  getIsLoggingIn(): boolean {
    return this._isLoggingIn()
  }

  setUser(user: User | null): void {
    this._user.set(user)
    if (user) {
      this.persistUserData(user)
    } else {
      this.clearPersistedUserData()
    }
  }

  setUserFromLoginResponse(response: LoginSuccessResponse): void {
    const user: User = {
      id: response.id,
      email: response.email,
      displayName: response.username,
      avatar: response.avatar,
      role: response.role as UserRole | null,
      language: response.language,
      groups: response.groups,
      tenantId: response.tenant_id
    }
    this.setUser(user)
  }

  setUserFromMeResponse(response: MeResponse): void {
    const user: User = {
      id: response.id,
      email: response.email,
      displayName: response.display_name,
      avatar: response.avatar,
      role: response.role as UserRole | null,
      language: response.language,
      groups: response.groups,
      tenantId: response.tenant.id
    }
    this.setUser(user)
  }

  getUserLanguage(): string | undefined {
    return this._user()?.language ?? undefined
  }

  clearAuth(): void {
    this._user.set(null)
    this.clearPersistedUserData()
  }

  hasStoredSession(): boolean {
    return this.getStoredUserData() !== null
  }

  getStoredUserData(): User | null {
    try {
      const stored = localStorage.getItem(USER_DATA_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }

  restoreSessionFromStorage(): boolean {
    const userData = this.getStoredUserData()
    if (userData) {
      this._user.set(userData)
      return true
    }
    return false
  }

  private persistUserData(user: User): void {
    try {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(user))
    } catch {
      console.warn('Failed to persist user data to localStorage')
    }
  }

  private clearPersistedUserData(): void {
    try {
      localStorage.removeItem(USER_DATA_KEY)
    } catch {
      // Ignore errors
    }
  }
}
